import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Camera } from '@babylonjs/core/Cameras/camera';
import { Ray } from '@babylonjs/core/Culling/ray';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths';
import { MeshNode } from '@models/classes/meshes/mesh-node';
import { KbQuaternion } from '@models/classes/primitives/kb-quaternion';
import type { IKbVectorLike } from '@models/classes/primitives/kb-vector';

const ray1 = Ray.Zero();
const vector1 = Vector3.Zero();
const vector2 = Vector3.Zero();
const vector3 = Vector3.Zero();

export type RoundDirection = 'ceil' | 'floor' | 'round';

export function getCameraPositionWithOffset(camera: Camera) {
    if (camera instanceof ArcRotateCamera) {
        camera.getForwardRayToRef(ray1);
        Vector3.CrossToRef(ray1.direction, Vector3.Up(), vector1);
        Vector3.CrossToRef(ray1.direction, vector1, vector2);
        vector1.normalize().scaleInPlace(-camera.targetScreenOffset.x);
        vector2.normalize().scaleInPlace(camera.targetScreenOffset.y);
        vector3.copyFrom(camera.globalPosition);
        vector3.addInPlace(vector1).addInPlace(vector2);

        return vector3;
    } else {
        return camera.globalPosition;
    }
}

export function toRadians(degrees: number) {
    const scalar = Math.PI / 180;
    return degrees * scalar;
}

export function roundVectorToPlaces(vector: Vector3, significance = 1, direction?: RoundDirection) {
    vector.x = roundToPlaces(vector.x, significance, direction);
    vector.y = roundToPlaces(vector.y, significance, direction);
    vector.z = roundToPlaces(vector.z, significance, direction);
}

export function roundToPlaces(value: number, significance = 1, direction: RoundDirection = 'round') {
    const isRound = Math.round(value / significance) * significance === value;
    if (isRound) {
        return value;
    }
    const n = (value / significance) * (1 + Number.EPSILON);
    let tmp: number;
    if (direction === 'ceil') {
        tmp = Math.ceil(n);
    } else if (direction === 'floor') {
        tmp = Math.floor(n);
    } else {
        tmp = Math.round(n);
    }
    return tmp * significance;
}

export function rotationToVector(toVector: Vector3, fromVector?: Vector3) {
    const result = new Quaternion();
    if (!fromVector) {
        fromVector = new Vector3(0, 1, 0);
    }
    rotationToVectorToRef(toVector, fromVector, result);

    return result;
}

export function transformVectorWithQuat(vector: Vector3, quat: Quaternion) {
    const transform = new Matrix();
    quat.toRotationMatrix(transform);
    const transformVector = Vector3.TransformCoordinates(vector, transform);
    return transformVector;
}

export function applyTransformToVector(vector: IKbVectorLike, transform: Matrix) {
    const v = new Vector3(vector.x, vector.y, vector.z);
    const matrix = Matrix.Compose(new Vector3(1, 1, 1), Quaternion.Identity(), v);
    return applyTransformToSpace(matrix, transform);
}

export function applyTransformToSpace(worldTransform: Matrix | number[], inSpace?: Matrix) {
    if (!inSpace) {
        inSpace = Matrix.Identity();
    }
    const inSpaceInv = inSpace.clone().invert();

    if (Array.isArray(worldTransform)) {
        worldTransform = Matrix.FromArray(worldTransform);
    }

    const spaceTransform = inSpace.multiply(worldTransform);
    spaceTransform.multiplyToRef(inSpaceInv, spaceTransform);

    const t = new Vector3();
    const q = new Quaternion();
    const s = new Vector3();
    spaceTransform.decompose(s, q, t);

    return {
        position: { x: t.x, y: t.y, z: t.z } as IKbVectorLike,
        rotation: KbQuaternion.FromQuat(q),
        scaling: { x: s.x, y: s.y, z: s.z } as IKbVectorLike,
    };

    // const fromSpaceRotation = fromSpace.rotationQuaternion ? fromSpace.rotationQuaternion : Quaternion.Identity();
    // const toSpaceRotation = toSpace.rotationQuaternion ? toSpace.rotationQuaternion : Quaternion.Identity();

    // const fromSpaceRotationInverse = fromSpaceRotation.clone().invert();
    // const toSpaceRotationInverse = toSpaceRotation.clone().invert();

    // const q1 = q.clone().multiply(fromSpaceRotationInverse);
    // const q2 = toSpaceRotationInverse.multiply(q1);
    // const q3 = q2.multiply(toSpaceRotation);

    // return q3;
}

export function computeLocalTransform(node: MeshNode) {
    let rotation: Quaternion;
    if (node.useEuler) {
        rotation = Quaternion.FromEulerAngles(
            toRadians(node.eulerRotation.x),
            toRadians(node.eulerRotation.y),
            toRadians(node.eulerRotation.z)
        );
    } else {
        rotation = Quaternion.RotationAxis(node.rotationAxis.toVec3(), node.rotationAngle * (Math.PI / 180));
    }

    const pivotTransform = node.pivot ? Matrix.Translation(-node.pivot.x, -node.pivot.y, -node.pivot.z) : undefined;

    const scalingTransform = Matrix.Scaling(node.scaling, node.scaling, node.scaling);
    const rotationTransform = new Matrix();
    Matrix.FromQuaternionToRef(rotation, rotationTransform);
    const translationTransform = Matrix.Translation(node.position.x, node.position.y, node.position.z);

    const transform = Matrix.Identity();
    if (pivotTransform) {
        transform.multiplyToRef(pivotTransform, transform);
    }
    transform.multiplyToRef(scalingTransform, transform);
    transform.multiplyToRef(rotationTransform, transform);
    transform.multiplyToRef(translationTransform, transform);
    if (pivotTransform) {
        transform.multiplyToRef(pivotTransform.invert(), transform);
    }

    return transform;
}

export function rotationToVectorToRef(_toVector: Vector3, _fromVector: Vector3, result: Quaternion) {
    const toVector = _toVector.clone().normalize();
    const fromVector = _fromVector.clone().normalize();

    if (Vector3.Dot(fromVector, toVector) > 0.9999999) {
        result.set(0, 0, 0, 1); // Identity Quaternion
    }
    if (Vector3.Dot(fromVector, toVector) < -0.9999999) {
        // Rotate 180 degrees about arbitrary axis
        return Quaternion.RotationAxisToRef(
            Vector3.Right().equals(toVector) ? Vector3.Up().cross(toVector) : Vector3.Right().cross(toVector),
            Math.PI,
            result
        );
    }
    const rotAxis = toVector.cross(fromVector);
    const w = -Math.acos(Vector3.Dot(fromVector, toVector));

    return Quaternion.RotationAxisToRef(rotAxis, w, result);
}

export function degreeToRadian(vectorDegree: Vector3, optionalResult?: Vector3): Vector3 {
    optionalResult = optionalResult || new Vector3();
    if (vectorDegree !== optionalResult) {
        optionalResult.copyFrom(vectorDegree);
    }
    return multiplyScalar(optionalResult, Math.PI / 180);
}

export function multiplyScalar(vec: Vector3, scalar: number): Vector3 {
    vec.x *= scalar;
    vec.y *= scalar;
    vec.z *= scalar;
    return vec;
}

export function computeNormal(normals: Vector3[]): Vector3 {
    const vertex1 = normals[0];
    const vertex2 = normals[1];
    const vertex3 = normals[2];
    const p1p2 = vertex1.subtract(vertex2);
    const p3p2 = vertex3.subtract(vertex2);

    const normal = Vector3.Normalize(Vector3.Cross(p1p2, p3p2));
    return normal;
}

export function applyMatrix4(thisVect: Vector3, m: Matrix) {
    const x = thisVect.x,
        y = thisVect.y,
        z = thisVect.z;
    const e = m.asArray();
    thisVect.x = e[0] * x + e[4] * y + e[8] * z + e[12];
    thisVect.y = e[1] * x + e[5] * y + e[9] * z + e[13];
    thisVect.z = e[2] * x + e[6] * y + e[10] * z + e[14];
    return thisVect;
}

export function mathClamp(x: number, a: number, b: number): number {
    return x < a ? a : x > b ? b : x;
}

export function makeRotationAxis(axis: Vector3, angle: number): Matrix {
    const c = Math.cos(angle);
    const s = Math.sin(angle);
    const t = 1 - c;
    const x = axis.x,
        y = axis.y,
        z = axis.z;
    const tx = t * x,
        ty = t * y;
    const matrix = Matrix.FromValues(
        tx * x + c,
        tx * y - s * z,
        tx * z + s * y,
        0,
        tx * y + s * z,
        ty * y + c,
        ty * z - s * x,
        0,
        tx * z - s * y,
        ty * z + s * x,
        t * z * z + c,
        0,
        0,
        0,
        0,
        1
    );
    return matrix;
}

export function getRotationBtwVectors(u: Vector3, v: Vector3) {
    //http://lolengine.net/blog/2014/02/24/quaternion-from-two-vectors-final
    v.clone().normalize();
    u.clone().normalize();
    const dot = Vector3.Dot(u, v);
    const k = 1; //we are using unit vectors, so just use "1" instead of:  Math.sqrt(u.length()^2 * v.length()^2);
    let w = k + dot;
    let r: Vector3;

    if (w < 0.0000001) {
        /* If u and v are exactly opposite, rotate 180 degrees
         * around an arbitrary orthogonal axis. Axis normalisation
         * can happen later, when we normalise the quaternion. */
        w = 0;
        r = Math.abs(u.x) > Math.abs(u.z) ? new Vector3(-u.y, u.x, 0) : new Vector3(0, -u.z, u.y);
    } else {
        r = Vector3.Cross(u, v);
    }

    return new Quaternion(r.x, r.y, r.z, w).normalize();
}

export function addArrays(arrayA: number[], arrayB: number[]) {
    if (arrayA.length == 0) {
        return arrayB;
    }
    if (arrayB.length == 0) {
        return arrayA;
    }
    const result = [];
    const n = Math.min(arrayA.length, arrayB.length);
    for (let i = 0; i < n; i++) {
        result[i] = arrayA[i] + arrayB[i];
    }
    return result;
}

export enum AxisType {
    X = 1,
    Y = 2,
    Z = 3,
    Custom = 4,
}
